import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { VideoSection } from '../components/VideoSection';
import { toast } from 'sonner';
import { 
  portfolioItems, 
  services, 
  differentiators, 
  blogPosts 
} from '../data/mock';

const fadeInUp = {
  initial: { opacity: 0, y: 60 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: [0.4, 0, 0.2, 1] }
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

const Landing = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    toast.success('Message sent! We\'ll be in touch soon.');
    setFormData({ name: '', email: '', company: '', message: '' });
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="bg-black text-white">
      {/* Hero Section - No Video Background */}
      <section className="min-h-screen flex items-center justify-center px-6 lg:px-12 pt-20 relative overflow-hidden bg-black">
        <motion.div 
          className="max-w-5xl mx-auto text-center relative z-10"
        >
          <motion.h1 
            className="text-5xl md:text-7xl lg:text-8xl font-playfair font-normal tracking-tight mb-8 leading-tight"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Bridal and couture visuals,
            <br />
            <span className="text-white/60">with 98% garment-perfect virtual try-on.</span>
          </motion.h1>
          <motion.p 
            className="text-xl md:text-2xl text-white/60 mb-12 font-light max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            AI-powered production for fashion brands, boutiques, and e-commerce.
          </motion.p>
          <motion.div 
            className="flex flex-col sm:flex-row gap-4 justify-center"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            <Button 
              size="lg" 
              className="bg-white text-black hover:bg-white/90 text-base tracking-wide px-8 py-6"
              onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
            >
              Get a Sample
              <ArrowRight className="ml-2" size={20} />
            </Button>
            <Button 
              size="lg" 
              variant="outline"
              className="border-white/20 text-white hover:bg-white hover:text-black text-base tracking-wide px-8 py-6"
              onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
            >
              Book a Paid Test
            </Button>
          </motion.div>
        </motion.div>
      </section>

      {/* Video Section - Full Brightness Below Hero */}
      <VideoSection 
        src="/videos/couture.mp4" 
        poster="/videos/couture-poster.jpg" 
        testId="landing-video-section" 
      />

      {/* Portfolio Section */}
      <section className="py-24 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl md:text-5xl font-playfair font-normal tracking-tight mb-4">Portfolio</h2>
            <p className="text-white/60 text-lg">Before → After transformations</p>
          </motion.div>
          
          <div className="space-y-24">
            {portfolioItems.map((item, index) => (
              <motion.div 
                key={item.id} 
                className="grid md:grid-cols-2 gap-8 items-center"
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.7, delay: index * 0.1 }}
              >
                <motion.div 
                  className={`space-y-4 ${index % 2 === 1 ? 'md:order-2' : ''}`}
                  whileHover={{ x: index % 2 === 1 ? 10 : -10 }}
                  transition={{ duration: 0.3 }}
                >
                  <span className="text-sm text-white/40 tracking-wider">{item.category}</span>
                  <h3 className="text-3xl font-playfair font-normal tracking-tight">{item.title}</h3>
                  <p className="text-white/60 text-lg">{item.description}</p>
                  <div className="flex items-center space-x-4 pt-4">
                    <div className="text-center">
                      <div className="text-sm text-white/40 mb-2">BEFORE</div>
                      <motion.div 
                        className="w-20 h-1 bg-white/20"
                        whileHover={{ scaleX: 1.2 }}
                        transition={{ duration: 0.3 }}
                      />
                    </div>
                    <ArrowRight className="text-white/40" size={20} />
                    <div className="text-center">
                      <div className="text-sm text-white/40 mb-2">AFTER</div>
                      <motion.div 
                        className="w-20 h-1 bg-white"
                        whileHover={{ scaleX: 1.2 }}
                        transition={{ duration: 0.3 }}
                      />
                    </div>
                  </div>
                </motion.div>
                
                <div className={`grid grid-cols-2 gap-4 ${index % 2 === 1 ? 'md:order-1' : ''}`}>
                  <motion.div 
                    className="relative aspect-[3/4] overflow-hidden bg-white/5"
                    whileHover={{ scale: 1.05 }}
                    transition={{ duration: 0.4 }}
                  >
                    <img 
                      src={item.beforeImage} 
                      alt={`${item.title} before`}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-4 left-4 px-3 py-1 bg-black/80 backdrop-blur-sm text-xs tracking-wider">
                      BEFORE
                    </div>
                  </motion.div>
                  <motion.div 
                    className="relative aspect-[3/4] overflow-hidden bg-white/5"
                    whileHover={{ scale: 1.05 }}
                    transition={{ duration: 0.4 }}
                  >
                    <img 
                      src={item.afterImage} 
                      alt={`${item.title} after`}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-4 left-4 px-3 py-1 bg-white text-black text-xs tracking-wider">
                      AFTER
                    </div>
                  </motion.div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 px-6 lg:px-12 bg-white/5">
        <div className="max-w-7xl mx-auto">
          <motion.h2 
            className="text-4xl md:text-5xl font-playfair font-normal tracking-tight mb-16 text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            What We Deliver
          </motion.h2>
          
          <motion.div 
            className="grid md:grid-cols-3 gap-8"
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            {services.map((service, index) => (
              <motion.div
                key={service.id}
                variants={fadeInUp}
                whileHover={{ y: -10 }}
                transition={{ duration: 0.3 }}
              >
                <Card className="bg-black border-white/10 hover:border-white/30 transition-all h-full">
                  <CardContent className="p-8">
                    <h3 className="text-xl font-light tracking-tight mb-4 text-white">
                      {service.title}
                    </h3>
                    <p className="text-white/60 leading-relaxed">
                      {service.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Why Us Section */}
      <section className="py-24 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <motion.h2 
            className="text-4xl md:text-5xl font-playfair font-normal tracking-tight mb-16 text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            Why Ruminating Room
          </motion.h2>
          
          <motion.div 
            className="grid md:grid-cols-2 gap-x-16 gap-y-8 max-w-4xl mx-auto"
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            {differentiators.map((item) => (
              <motion.div 
                key={item.id} 
                className="flex items-start space-x-4"
                variants={fadeInUp}
                whileHover={{ x: 10 }}
                transition={{ duration: 0.3 }}
              >
                <div className="flex-shrink-0 w-6 h-6 rounded-full border border-white/20 flex items-center justify-center mt-1">
                  <Check size={14} className="text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-light tracking-tight mb-2">{item.title}</h3>
                  <p className="text-white/60 text-sm leading-relaxed">{item.description}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-24 px-6 lg:px-12 bg-white/5">
        <motion.div 
          className="max-w-3xl mx-auto text-center"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-4xl md:text-5xl font-playfair font-normal tracking-tight mb-8">About</h2>
          <p className="text-xl text-white/60 leading-relaxed">
            Ruminating Room is a creative agency for bridal and couture brands that delivers premium virtual try-on, campaign visuals, and editorial imagery without photoshoot logistics.
          </p>
        </motion.div>
      </section>

      {/* Blog Section */}
      <section className="py-24 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <motion.h2 
            className="text-4xl md:text-5xl font-playfair font-normal tracking-tight mb-16 text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            Insights
          </motion.h2>
          
          <motion.div 
            className="grid md:grid-cols-3 gap-8"
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            {blogPosts.map((post) => (
              <motion.div
                key={post.id}
                variants={fadeInUp}
                whileHover={{ y: -10 }}
                transition={{ duration: 0.3 }}
              >
                <Card className="bg-black border-white/10 hover:border-white/30 transition-all cursor-pointer group h-full">
                  <CardContent className="p-8">
                    <div className="text-xs text-white/40 tracking-wider mb-4">{post.date}</div>
                    <h3 className="text-xl font-light tracking-tight mb-3 group-hover:text-white/80 transition-colors">
                      {post.title}
                    </h3>
                    <p className="text-white/60 text-sm leading-relaxed mb-4">
                      {post.excerpt}
                    </p>
                    <div className="flex items-center text-sm text-white/60 group-hover:text-white transition-colors">
                      Read more
                      <ArrowRight size={16} className="ml-2" />
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 px-6 lg:px-12 bg-white/5">
        <div className="max-w-2xl mx-auto">
          <motion.div 
            className="text-center mb-12"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl md:text-5xl font-playfair font-normal tracking-tight mb-4">Get in Touch</h2>
            <p className="text-white/60 text-lg">Let's create something exceptional together.</p>
          </motion.div>
          
          <motion.form 
            onSubmit={handleSubmit} 
            className="space-y-6"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="grid md:grid-cols-2 gap-6">
              <Input
                type="text"
                name="name"
                placeholder="Your Name"
                value={formData.name}
                onChange={handleChange}
                required
                className="bg-black border-white/20 text-white placeholder:text-white/40 focus:border-white"
              />
              <Input
                type="email"
                name="email"
                placeholder="Email Address"
                value={formData.email}
                onChange={handleChange}
                required
                className="bg-black border-white/20 text-white placeholder:text-white/40 focus:border-white"
              />
            </div>
            
            <Input
              type="text"
              name="company"
              placeholder="Company / Brand"
              value={formData.company}
              onChange={handleChange}
              className="bg-black border-white/20 text-white placeholder:text-white/40 focus:border-white"
            />
            
            <Textarea
              name="message"
              placeholder="Tell us about your project..."
              value={formData.message}
              onChange={handleChange}
              required
              rows={6}
              className="bg-black border-white/20 text-white placeholder:text-white/40 focus:border-white resize-none"
            />
            
            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
              <Button 
                type="submit"
                size="lg"
                className="w-full bg-white text-black hover:bg-white/90 text-base tracking-wide py-6"
              >
                Send Message
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </motion.div>
          </motion.form>
          
          <motion.div 
            className="mt-8 text-center"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <p className="text-white/60 text-sm mb-2">Or reach us directly</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 text-sm">
              <a href="mailto:contact.ruminatingroom@gmail.com" className="text-white hover:text-white/80 transition-colors">
                contact.ruminatingroom@gmail.com
              </a>
              <span className="hidden sm:inline text-white/40">|</span>
              <a href="https://wa.me/" className="text-white hover:text-white/80 transition-colors">
                WhatsApp
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Landing;
